import React, { useEffect, useState } from 'react';
import {
  RefreshCw, FileText, Download, Trash2, Play, AlertTriangle, CheckCircle2, Plus,
} from 'lucide-react';
// Phase 5B — Modal import dropped (Generate report now uses Slideout).
import { Button, Pill, Card, EmptyState, Slideout } from '../../index';
import { StatusBadge } from '../widgets/StatusBadge';
import { ccGenerateReport, ccListReports, ccGetReport } from '../../../../services/api';
/* G395 — Pagination for the compliance reports list. Reports are archived
 * indefinitely so the list grows monotonically; without paging the slideout
 * body becomes unscrollable on the in-slideout layout. */
import { Pagination, usePaginationState } from '../../../common/Pagination';

/**
 * ReportsSlideout — V2 Compliance Reports (Phase B6 / G319).
 *
 * Replaces ?legacy=1&tab=REPORTS. Production V2 workspace for
 * generating + managing compliance evidence packages.
 *
 *   - Filter by report type (SOC 2 / HIPAA / PCI / ISO 27001 / Custom)
 *   - Generator modal: pick framework + evidence scope → Temporal OSS job
 *   - Table: name | type | status (READY/GENERATING/FAILED) | created
 *     | actions (Download / Delete / Regenerate)
 *   - Auto-polls every 10s while any report is GENERATING so the
 *     operator sees the status flip without manual refresh
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
const REPORT_TYPES = [
  { value: '',         label: 'All types' },
  { value: 'SOC2',     label: 'SOC 2' },
  { value: 'HIPAA',    label: 'HIPAA' },
  { value: 'PCI',      label: 'PCI DSS' },
  { value: 'ISO27001', label: 'ISO 27001' },
  { value: 'CUSTOM',   label: 'Custom' },
];

export function ReportsSlideout({ isOpen, onClose, addToast }) {
  const [items, setItems] = useState([]);
  /* G395 — Paginated to avoid the slideout body becoming unscrollable
   * when the reports list grows past 30-50 entries. */
  const pgReports = usePaginationState(10);
  useEffect(() => { pgReports.setPage(0); }, [items.length]); // eslint-disable-line
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [generateModal, setGenerateModal] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ccListReports({ type: typeFilter || undefined });
      const list = Array.isArray(r) ? r : (r?.data || r?.items || r?.reports || []);
      setItems(Array.isArray(list) ? list : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isOpen) reload(); /* eslint-disable-next-line */ }, [isOpen, typeFilter]);

  /* Poll every 10s while any report is GENERATING so the operator
   * sees the status transition without manually hitting refresh. */
  useEffect(() => {
    if (!isOpen) return;
    const hasGenerating = items.some((r) => String(r.status || '').toUpperCase() === 'GENERATING');
    if (!hasGenerating) return;
    const t = setInterval(reload, 10_000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [isOpen, items]);

  const handleDownload = (r) => {
    /* Streamed binary — let the browser handle Content-Disposition. */
    window.open(`/api/v1/compliance-center/reports/${r.id}/download`, '_blank');
    addToast?.(`Downloading ${r.name || r.id}.`, 'success');
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={820}
      ariaLabel="Reports workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileText size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Compliance reports</h2>
            <Pill tone="cyan" size="sm">{items.length}</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0 mb-3">
            Generate evidence packages — SOC 2 / HIPAA / PCI / ISO 27001. Reports run as
            Temporal OSS workflows; once READY they're downloadable PDFs / ZIP archives.
          </p>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted">Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                    className="border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface text-[12.5px]">
              {REPORT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                    className="ml-auto" onClick={() => setGenerateModal(true)}>
              Generate report
            </Button>
            <Button variant="ghost" size="sm"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && items.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading reports…</p>
      )}
      {!loading && items.length === 0 && (
        <EmptyState
          icon={<FileText size={20} />}
          title="No reports yet"
          description="Generate your first compliance evidence package."
          primaryAction={
            <Button variant="primary" iconLeft={<Plus size={14} />} onClick={() => setGenerateModal(true)}>
              Generate report
            </Button>
          }
        />
      )}
      {items.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-v2-border-subtle">
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Report</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Type</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Status</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Created</th>
              <th className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pgReports.paginate(items).map((r) => {
              const status = String(r.status || 'READY').toUpperCase();
              const isGenerating = status === 'GENERATING';
              const isFailed = status === 'FAILED';
              return (
                <tr key={r.id} className="border-b border-v2-border-subtle/50 hover:bg-v2-muted/40">
                  <td className="py-2">
                    <div className="font-semibold text-v2-ink truncate max-w-[260px]">
                      {r.name || r.title || `Report ${String(r.id).slice(0, 8)}`}
                    </div>
                    {r.scope && (
                      <div className="text-[10.5px] text-v2-ink-muted truncate max-w-[260px]">{r.scope}</div>
                    )}
                  </td>
                  <td className="py-2"><Pill tone="cyan" size="sm">{r.type || r.frameworkType || '—'}</Pill></td>
                  <td className="py-2">
                    <span className="inline-flex items-center gap-1">
                      <StatusBadge status={status} />
                      {isGenerating && <RefreshCw size={11} className="animate-spin text-v2-ink-muted" />}
                    </span>
                    {isFailed && r.errorMessage && (
                      <div className="text-[10.5px] text-v2-danger mt-0.5 max-w-[200px] truncate" title={r.errorMessage}>
                        {r.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="py-2 font-mono-v2 text-[11px] text-v2-ink-muted">
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      {status === 'READY' && (
                        <Button variant="ghost" size="sm" iconLeft={<Download size={11} />}
                                onClick={() => handleDownload(r)}>
                          Download
                        </Button>
                      )}
                      {isFailed && (
                        <Button variant="ghost" size="sm" iconLeft={<Play size={11} />}
                                disabled={busy === r.id}
                                onClick={async () => {
                                  setBusy(r.id);
                                  try {
                                    const res = await ccGenerateReport({
                                      type: r.type, regenerateFrom: r.id, scope: r.scope,
                                    });
                                    if (res?.ok === false) throw new Error(res.error);
                                    addToast?.('Regeneration kicked off.', 'success');
                                    reload();
                                  } catch (e) { addToast?.(`Regenerate failed: ${e?.message || e}`, 'error'); }
                                  finally { setBusy(null); }
                                }}>
                          Retry
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {items.length > pgReports.pageSize && (
        <div className="mt-3">
          <Pagination
            totalItems={items.length}
            page={pgReports.page}
            pageSize={pgReports.pageSize}
            onPageChange={pgReports.setPage}
            onPageSizeChange={pgReports.setPageSize}
            noun="reports"
          />
        </div>
      )}

      {generateModal && (
        <GenerateReportModal
          onClose={() => setGenerateModal(false)}
          onSubmit={async ({ type, scope, agentScope }) => {
            const r = await ccGenerateReport({ type, scope, agentScope });
            if (r?.ok === false) throw new Error(r.error || 'Failed');
            addToast?.(`Report generation started — auto-refreshing.`, 'success');
            reload();
          }}
        />
      )}
    </Slideout>
  );
}

function GenerateReportModal({ onClose, onSubmit }) {
  const [type, setType] = useState('SOC2');
  const [scope, setScope] = useState('full');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  return (
    /* Phase 5B — Generate report: Modal → Slideout per G378. */
    <Slideout isOpen onClose={submitting ? undefined : onClose} width={560}
              ariaLabel="Generate report"
              header={<h2 className="text-[15px] font-bold m-0 inline-flex items-center gap-2"><FileText size={16} className="text-v2-action" /> Generate report</h2>}
              footer={
                <div className="flex items-center justify-end gap-2 w-full">
                  <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                  <Button variant="primary" disabled={submitting}
                          onClick={async () => {
                            setSubmitting(true); setError(null);
                            try { await onSubmit({ type, scope }); onClose(); }
                            catch (e) { setError(e?.message || 'Generation failed'); }
                            finally { setSubmitting(false); }
                          }}>
                    {submitting ? 'Starting…' : 'Generate'}
                  </Button>
                </div>
              }>
      <p className="text-[12.5px] text-v2-ink mb-4">
        Kicks off a Temporal OSS workflow that gathers evidence, runs control tests, and
        packages the result as a downloadable artefact. Generation typically takes 2-5
        minutes; refresh this slideout or wait for the auto-refresh.
      </p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Framework</span>
          <select value={type} onChange={(e) => setType(e.target.value)}
                  className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface">
            <option value="SOC2">SOC 2 Type II</option>
            <option value="HIPAA">HIPAA</option>
            <option value="PCI">PCI DSS</option>
            <option value="ISO27001">ISO 27001</option>
            <option value="CUSTOM">Custom framework</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Evidence scope</span>
          <select value={scope} onChange={(e) => setScope(e.target.value)}
                  className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface">
            <option value="full">Full — all controls + attestations + audit trail</option>
            <option value="summary">Summary — top-level posture only</option>
            <option value="custom">Custom — pick controls (advanced)</option>
          </select>
        </label>
      </div>
      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger">
          {error}
        </div>
      )}
    </Slideout>
  );
}

export default ReportsSlideout;
